import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ShoppingCart, Menu, X, LogOut, User } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useCheckoutBundleStore } from '../store/checkoutBundleStore'
import { useAuthStore } from '../store/authStore'

function OfferDropdown({ onNavigate }) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const timer = useRef(null)

  const items = [
    { to: '/subscription/personal', label: t('nav.individualSubscription') },
    { to: '/subscriptions/thematic', label: t('nav.thematicSubscriptions') },
    { to: '/gifts', label: t('nav.giftCards') },
    { to: '/company', label: t('nav.companyOffer') },
  ]

  const show = () => {
    clearTimeout(timer.current)
    setOpen(true)
  }

  const hide = () => {
    timer.current = setTimeout(() => setOpen(false), 400)
  }

  useEffect(() => {
    return () => clearTimeout(timer.current)
  }, [])

  return (
    <div
      className="relative"
      onMouseEnter={show}
      onMouseLeave={hide}
    >
      <button
        type="button"
        className="font-bold text-brand-blue hover:text-brand-orange transition py-2"
        aria-expanded={open}
      >
        {t('nav.offer')}
      </button>
      {open && (
        <div className="absolute top-full left-1/2 -translate-x-1/2 pt-3">
          <div className="bg-brand-gold rounded-3xl shadow-xl border border-brand-orange/20 py-2 px-4 min-w-[220px]">
            {items.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={() => {
                  setOpen(false)
                  onNavigate?.()
                }}
                className={({ isActive }) =>
                  `block py-2 text-brand-blue hover:text-brand-orange transition font-bold ${
                    isActive ? 'text-brand-orange' : ''
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

function AuthPill({ onNavigate }) {
  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const { user, logout } = useAuthStore()
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const timer = useRef(null)

  const displayName = user?.first_name || user?.f_name || user?.email || ''

  const open = () => {
    clearTimeout(timer.current)
    setDropdownOpen(true)
  }

  const close = () => {
    timer.current = setTimeout(() => setDropdownOpen(false), 400)
  }

  useEffect(() => {
    return () => clearTimeout(timer.current)
  }, [])

  const handleLogout = async () => {
    setDropdownOpen(false)
    await logout()
    onNavigate?.()
    navigate('/')
  }

  if (!user) {
    return (
      <NavLink
        to={`/login?returnUrl=${encodeURIComponent(location.pathname + location.search)}`}
        className={({ isActive }) =>
          `inline-flex items-center rounded-full px-4 py-2 font-bold text-sm md:text-base transition ${
            isActive
              ? 'bg-brand-blue/90 text-brand-gold'
              : 'bg-brand-blue text-brand-gold hover:bg-brand-blue/90'
          }`
        }
      >
        {t('nav.login')}
      </NavLink>
    )
  }

  return (
    <div
      className="relative"
      onMouseEnter={open}
      onMouseLeave={close}
    >
      <button
        type="button"
        className="inline-flex items-center gap-2 rounded-full px-4 py-2 font-bold text-sm md:text-base bg-brand-blue text-brand-gold hover:bg-brand-blue/90 transition"
        aria-expanded={dropdownOpen}
      >
        <span>{displayName}</span>
        <User size={18} aria-hidden="true" />
      </button>

      {dropdownOpen && (
        <div className="absolute top-full right-0 bg-brand-blue rounded-2xl shadow-xl border border-brand-gold/20 py-2 px-3 min-w-[160px] z-50 pt-2">
          {user?.isAdmin && (
            <a
              href="/admin/dashboard"
              onClick={() => {
                setDropdownOpen(false)
                onNavigate?.()
              }}
              className="block w-full text-left px-2 py-2 text-brand-gold font-bold hover:text-brand-orange transition"
            >
              {t('nav.adminPanel')}
            </a>
          )}
          <NavLink
            to="/moja-biblioteka/profil"
            onClick={() => {
              setDropdownOpen(false)
              onNavigate?.()
            }}
            className="block w-full text-left px-2 py-2 text-brand-gold font-bold hover:text-brand-orange transition"
          >
            {t('nav.profile')}
          </NavLink>
          <button
            type="button"
            onClick={handleLogout}
            className="block w-full text-left px-2 py-2 text-brand-gold font-bold hover:text-brand-orange transition"
          >
            {t('nav.logout')}
          </button>
        </div>
      )}
    </div>
  )
}

export default function Header() {
  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const [mobileOpen, setMobileOpen] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const [initialRevealDone, setInitialRevealDone] = useState(false)
  const menuTimer = useRef(null)
  const totalItems = useCheckoutBundleStore((state) => state.getTotalItemCount())

  const isHome = location.pathname === '/'
  const showHeader = isHome ? menuOpen || !initialRevealDone : true

  useEffect(() => {
    if (!isHome) return
    const timer = setTimeout(() => setInitialRevealDone(true), 10000)
    return () => clearTimeout(timer)
  }, [isHome])

  const openMenu = () => {
    clearTimeout(menuTimer.current)
    if (isHome) setMenuOpen(true)
  }

  const closeMenu = () => {
    if (!isHome) return
    menuTimer.current = setTimeout(() => setMenuOpen(false), 1000)
  }

  useEffect(() => {
    return () => clearTimeout(menuTimer.current)
  }, [])

  const handleAboutClick = () => {
    setMobileOpen(false)
    if (location.pathname === '/') {
      const about = document.getElementById('about')
      if (about) {
        about.scrollIntoView({ behavior: 'smooth' })
      }
    } else {
      navigate('/#about')
    }
  }

  return (
    <div className="fixed top-0 left-0 right-0 z-50">
      {/* Homepage hover hot zone — visible only when the menu is hidden */}
      {isHome && !showHeader && (
        <div
          className="hidden md:block fixed top-0 left-0 right-0 h-8 z-[60]"
          onMouseEnter={openMenu}
        >
          <div className="absolute top-1 left-1/2 -translate-x-1/2 w-16 h-2 bg-brand-gold rounded-full" />
        </div>
      )}

      <div
        onMouseEnter={openMenu}
        onMouseLeave={closeMenu}
      >
        <header
          className={`fixed top-0 left-0 right-0 z-50 transition-transform duration-300 ease-out ${
            showHeader ? 'translate-y-0' : '-translate-y-full'
          }`}
        >
          {/* Desktop menu bar */}
          <div className="hidden lg:block max-w-6xl mx-auto mt-4 bg-brand-gold rounded-full px-4 md:px-6 py-2 shadow-lg">
            <div className="relative flex items-center justify-between gap-4">
              {/* Logo */}
              <Link to="/" className="flex items-center shrink-0">
                <span className="font-climate text-brand-blue text-base md:text-xl leading-none whitespace-nowrap inline-block origin-left scale-x-[0.8] tracking-[-0.04em]">
                  Abonamenty Literackie
                </span>
              </Link>

              {/* Desktop nav */}
              <nav className="flex flex-1 items-center justify-evenly text-brand-blue px-4">
                <button
                  type="button"
                  onClick={handleAboutClick}
                  className="font-bold hover:text-brand-orange transition py-2"
                >
                  {t('nav.aboutUs')}
                </button>

                <OfferDropdown />

                <NavLink
                  to="/moja-biblioteka"
                  className={({ isActive }) =>
                    `font-bold hover:text-brand-orange transition py-2 ${
                      isActive ? 'text-brand-orange' : ''
                    }`
                  }
                >
                  {t('nav.myLibrary')}
                </NavLink>

                <NavLink
                  to="/checkout"
                  className={({ isActive }) =>
                    `font-bold hover:text-brand-orange transition flex items-center gap-1 py-2 ${
                      isActive ? 'text-brand-orange' : ''
                    }`
                  }
                >
                  {t('nav.basket')}
                  {totalItems > 0 && (
                    <span className="bg-brand-blue text-brand-gold text-xs font-bold px-1.5 py-0.5 rounded-full">
                      {totalItems}
                    </span>
                  )}
                </NavLink>
              </nav>

              {/* Auth pill */}
              <div className="flex items-center shrink-0">
                <AuthPill onNavigate={() => setMobileOpen(false)} />
              </div>
            </div>
          </div>

          {/* Mobile floating hamburger */}
          <button
            type="button"
            className="lg:hidden fixed top-4 right-4 z-[60] bg-brand-gold text-brand-blue p-3 rounded-full shadow-lg transition-transform active:scale-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label={t('nav.toggleMenu')}
            aria-expanded={mobileOpen}
            aria-controls="mobile-menu"
          >
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>

          {/* Mobile menu */}
          {mobileOpen && (
            <div
              id="mobile-menu"
              className="lg:hidden fixed top-20 right-4 w-64 bg-brand-gold rounded-3xl shadow-xl border border-brand-orange/20 px-4 pb-4 z-[60]"
            >
              <nav className="flex flex-col gap-3 text-brand-blue pt-3">
                <button
                  type="button"
                  onClick={handleAboutClick}
                  className="text-left font-bold hover:text-brand-orange transition"
                >
                  {t('nav.aboutUs')}
                </button>

                <div className="font-bold">{t('nav.offer')}</div>
                <div className="pl-4 flex flex-col gap-2 border-l-2 border-brand-orange/30">
                  <NavLink to="/subscription/personal" onClick={() => setMobileOpen(false)} className="font-bold hover:text-brand-orange transition">
                    {t('nav.individualSubscription')}
                  </NavLink>
                  <NavLink to="/subscriptions/thematic" onClick={() => setMobileOpen(false)} className="font-bold hover:text-brand-orange transition">
                    {t('nav.thematicSubscriptions')}
                  </NavLink>
                  <NavLink to="/gifts" onClick={() => setMobileOpen(false)} className="font-bold hover:text-brand-orange transition">
                    {t('nav.giftCards')}
                  </NavLink>
                  <NavLink to="/company" onClick={() => setMobileOpen(false)} className="font-bold hover:text-brand-orange transition">
                    {t('nav.companyOffer')}
                  </NavLink>
                </div>

                <NavLink
                  to="/moja-biblioteka"
                  onClick={() => setMobileOpen(false)}
                  className={({ isActive }) =>
                    `font-bold hover:text-brand-orange transition ${
                      isActive ? 'text-brand-orange' : ''
                    }`
                  }
                >
                  {t('nav.myLibrary')}
                </NavLink>

                <NavLink
                  to="/checkout"
                  onClick={() => setMobileOpen(false)}
                  className={({ isActive }) =>
                    `font-bold hover:text-brand-orange transition flex items-center gap-2 ${
                      isActive ? 'text-brand-orange' : ''
                    }`
                  }
                >
                  {t('nav.basket')}
                  {totalItems > 0 && (
                    <span className="bg-brand-blue text-brand-gold text-xs font-bold px-1.5 py-0.5 rounded-full">
                      {totalItems}
                    </span>
                  )}
                </NavLink>

                <MobileAuthSection onNavigate={() => setMobileOpen(false)} />
              </nav>
            </div>
          )}
        </header>
      </div>
    </div>
  )
}

function MobileAuthSection({ onNavigate }) {
  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const { user, logout } = useAuthStore()

  const displayName = user?.first_name || user?.f_name || user?.email || ''

  if (!user) {
    return (
      <NavLink
        to={`/login?returnUrl=${encodeURIComponent(location.pathname + location.search)}`}
        onClick={onNavigate}
        className="inline-flex items-center justify-center gap-2 rounded-full px-4 py-2 font-bold bg-brand-blue text-brand-gold hover:bg-brand-blue/90 transition"
      >
        {t('nav.login')}
      </NavLink>
    )
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="inline-flex items-center gap-2 rounded-full px-4 py-2 font-bold bg-brand-blue text-brand-gold">
        <User size={18} />
        <span>{displayName}</span>
      </div>
      {user?.isAdmin && (
        <a
          href="/admin/dashboard"
          onClick={onNavigate}
          className="pl-4 font-bold hover:text-brand-orange transition"
        >
          {t('nav.adminPanel')}
        </a>
      )}
      <NavLink
        to="/moja-biblioteka/profil"
        onClick={onNavigate}
        className="pl-4 font-bold hover:text-brand-orange transition"
      >
        {t('nav.profile')}
      </NavLink>
      <button
        type="button"
        onClick={async () => {
          await logout()
          onNavigate?.()
          navigate('/')
        }}
        className="text-left pl-4 font-bold hover:text-brand-orange transition flex items-center gap-2"
      >
        <LogOut size={18} />
        {t('nav.logout')}
      </button>
    </div>
  )
}
